import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, Checkbox, Button, TextField } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function UserRoleAccessPicker({
  userAccessList = [],
  theme = "light",
  control,
  setValue,
  getValues,
  formFieldName = "userAccessIds",
  initialSelectedIds = [],
  disabled = false,
  title = "",
}) {
  const [availableAccess, setAvailableAccess] = useState([]);
  const [assignedAccess, setAssignedAccess] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const [availableSearch, setAvailableSearch] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");

  const filterAccess = (items, search) =>
    (items ?? []).filter(a => (a?.accessName || "").toLowerCase().includes((search || "").toLowerCase()));

  const handleToggle = (id, selectedList, setSelectedList) => {
    setSelectedList(selectedList.includes(id) ? selectedList.filter(x => x !== id) : [...selectedList, id]);
  };

  const handleSelectAll = (items, setSelected) => {
    setSelected((items ?? []).map(a => a.accessId));
  };

  const syncFormAccessIds = useCallback((nextAssigned) => {
    const ids = (nextAssigned ?? []).map(a => a.accessId);
    if (setValue) setValue(formFieldName, ids, { shouldDirty: true, shouldValidate: true });
  }, [setValue, formFieldName]);

  const moveAccess = (
    fromList, toList, selectedIds,
    setFromList, setToList, setSelected,
    toAssigned
  ) => {
    if (!selectedIds?.length || disabled) return;
    const moving = fromList.filter(a => selectedIds.includes(a.accessId));
    const updatedFrom = fromList.filter(a => !selectedIds.includes(a.accessId));
    const updatedTo   = [...toList, ...moving];

    setFromList(updatedFrom);
    setToList(updatedTo);
    setSelected([]);

    const nextAssigned = toAssigned ? updatedTo : updatedFrom;
    syncFormAccessIds(nextAssigned);
  };

  useEffect(() => {
    let currentIds = [];
    if (getValues) {
      const v = getValues(formFieldName);
      if (Array.isArray(v)) currentIds = v;
    }
    if (!currentIds?.length && Array.isArray(initialSelectedIds) && initialSelectedIds.length) {
      currentIds = initialSelectedIds;
      if (setValue) setValue(formFieldName, currentIds, { shouldDirty: false });
    }

    const selectedSet = new Set(currentIds ?? []);
    const assigned = (userAccessList ?? []).filter(a => selectedSet.has(a.accessId));
    const available  = (userAccessList ?? []).filter(a => !selectedSet.has(a.accessId));

    setAvailableAccess(available);
    setAssignedAccess(assigned);
    setSelectedAvailable([]);
    setSelectedAssigned([]);
    syncFormAccessIds(assigned);
  }, [userAccessList, getValues, setValue, formFieldName, initialSelectedIds, syncFormAccessIds]);

  const filteredAvailable  = useMemo(() => filterAccess(availableAccess, availableSearch), [availableAccess, availableSearch]);
  const filteredAssigned   = useMemo(() => filterAccess(assignedAccess, assignedSearch), [assignedAccess, assignedSearch]);

  return (
    <div className="rs-grid-wrapper">
      <Box sx={{ marginY: 2 }}>
        <Typography variant="h6" align="left" sx={{ color: theme === "light" ? "#000" : "#fff" }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ height: "620px", border: "2px solid #ccc", borderRadius: "8px", marginY: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <AccessList
            header="Access Denied"
            items={availableAccess}
            search={availableSearch}
            setSearch={setAvailableSearch}
            selected={selectedAvailable}
            setSelected={setSelectedAvailable}
            theme={theme}
            disabled={disabled}
            onSelectAll={() => handleSelectAll(filteredAvailable, setSelectedAvailable)}
            onItemToggle={handleToggle}
          />

          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 2 }}>
            <Button
              variant="contained"
              onClick={() =>
                moveAccess(
                  availableAccess, assignedAccess, selectedAvailable,
                  setAvailableAccess, setAssignedAccess, setSelectedAvailable, true
                )
              }
              disabled={selectedAvailable.length === 0 || disabled}
            >
              <ArrowForwardIcon />
            </Button>

            <Button
              variant="contained"
              onClick={() =>
                moveAccess(
                  assignedAccess, availableAccess, selectedAssigned,
                  setAssignedAccess, setAvailableAccess, setSelectedAssigned, false
                )
              }
              disabled={selectedAssigned.length === 0 || disabled}
            >
              <ArrowBackIcon />
            </Button>
          </Box>

          <AccessList
            header="Access Granted"
            items={assignedAccess}
            search={assignedSearch}
            setSearch={setAssignedSearch}
            selected={selectedAssigned}
            setSelected={setSelectedAssigned}
            theme={theme}
            disabled={disabled}
            onSelectAll={() => handleSelectAll(filteredAssigned, setSelectedAssigned)}
            onItemToggle={handleToggle}
          />
        </Box>
      </Box>
    </div>
  );
}

function AccessList({
  header,
  items,
  search,
  setSearch,
  selected,
  setSelected,
  theme,
  disabled,
  onSelectAll,
  onItemToggle,
}) {
  const filterItems = (arr, q) =>
    (arr ?? []).filter(a => (a?.accessName || "").toLowerCase().includes((q || "").toLowerCase()));

  const filtered = filterItems(items, search);

  return (
    <Box sx={{ width: "45%", height: "480px", border: "1px solid #ccc", borderRadius: "4px", padding: 4, margin: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", padding: 1, background: "#DFF0FF" }}>
        <Typography variant="subtitle2" align="left">
          {header}
        </Typography>
        {!disabled && (
          <Button size="small" onClick={onSelectAll}>
            Select All
          </Button>
        )}
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, mt: 2 }}
        disabled={disabled}
      />

      <List sx={{ maxHeight: "300px", overflowY: "auto", overflowX: "hidden", border: "1px solid #ccc", borderRadius: "4px", "& .MuiListItem-root": {
      px: 2}, }}>
        {filtered.map(item => {
          const checked = selected.includes(item.accessId);
          return (
            <ListItem
              key={item.accessId}
              dense
              disableGutters
              onClick={() => !disabled && onItemToggle(item.accessId, selected, setSelected)}
              secondaryAction={
                <Checkbox
                  edge="end"
                  checked={checked}
                  tabIndex={-1}
                  sx={{ px: 3 }}
                  disableRipple
                  onChange={() => !disabled && onItemToggle(item.accessId, selected, setSelected)}
                />
              }
            >
              <ListItemIcon><ListIcon /></ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ color: theme === "light" ? "inherit" : "common.white" }}
                primary={item.accessName}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
